<template>
  <div
    ref="rootRef"
    class="rich-html-case rich-html-case--geneticMetabolicBrainWhiteMatterJudgmentSystem"
  >
    <!-- Top Banner (poster-big.html) -->
    <section class="poster-banner">
      <div class="poster">
        <div class="bg-aura bg-aura--1"></div>
        <div class="bg-aura bg-aura--2"></div>
        <div class="dot-pattern"></div>

        <div class="typo-layer">
          <div class="typo-main">
            <span class="accent">遗传代谢性</span><br />
            脑白质<span class="accent">诊断</span>
          </div>
          <div class="typo-sub">
            Hereditary Leukoencephalopathy AI Diagnosis
          </div>
        </div>

        <div class="product-screenshot">
          <img
            class="product-screenshot__img"
            :src="bannerScreenshot"
            alt="诊断系统界面"
            @error="onImageError"
          />
        </div>

        <div class="info-block">
          <div class="info-block__title">智能辅助诊断 · 效率提升 80%</div>
          <div class="info-block__desc">
            基于深度学习的智能辅助诊断平台，覆盖 22+ 遗传代谢疾病模型。
            适用于神经内科、儿科、代谢科等六大临床科室。
          </div>
          <div class="info-block__tags">
            <span class="info-block__tag info-block__tag--solid"
              >深度学习诊断引擎</span
            >
            <span class="info-block__tag info-block__tag--light"
              >22+ 疾病模型</span
            >
            <span class="info-block__tag info-block__tag--light">多端协同</span>
          </div>
        </div>

        <div class="features">
          <div class="feature-card">
            <div class="feature-card__icon feature-card__icon--blue">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v6l4 2" />
              </svg>
            </div>
            <div class="feature-card__text">
              <div class="feature-card__title">诊断效率提升 80%</div>
              <div class="feature-card__desc">平均诊断时间从 48h → 8h</div>
            </div>
          </div>
          <div class="feature-card">
            <div class="feature-card__icon feature-card__icon--purple">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect x="3" y="3" width="18" height="18" rx="3" />
                <path d="M3 9h18" />
                <path d="M9 3v18" />
              </svg>
            </div>
            <div class="feature-card__text">
              <div class="feature-card__title">22+ 罕见疾病模型</div>
              <div class="feature-card__desc">六大学科领域全场景覆盖</div>
            </div>
          </div>
          <div class="feature-card">
            <div class="feature-card__icon feature-card__icon--green">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <rect x="5" y="2" width="14" height="20" rx="2" />
                <line x1="12" y1="18" x2="12.01" y2="18" />
              </svg>
            </div>
            <div class="feature-card__text">
              <div class="feature-card__title">多端实时协同</div>
              <div class="feature-card__desc">Web / iOS / Android 全平台</div>
            </div>
          </div>
        </div>

        <div class="stat-pill stat-pill--1">
          <span class="stat-pill__dot stat-pill__dot--blue"></span>
          准确率 94.6%
        </div>
        <div class="stat-pill stat-pill--2">
          <span class="stat-pill__dot stat-pill__dot--green"></span>
          已覆盖 12+ 三甲医院
        </div>

        <div class="deco-line"></div>
      </div>
    </section>

    <!-- Project Introduction -->
    <section class="project-intro">
      <div class="project-intro__inner">
        <img
          class="project-intro__title-img"
          :src="introduceText"
          alt="项目介绍"
        />
        <p class="project-intro__text fontRegular">{{ projectIntroduction }}</p>
      </div>
    </section>

    <div class="page-detail">
      <!-- ======================== BACKGROUND ======================== -->
      <section class="section section--pale">
        <div class="container">
          <div class="sec-header">
            <div class="sec-header__icon">
              <span class="sec-header__icon-dot"></span>
              Revision Background
            </div>
            <div class="sec-header__title">项目背景</div>
            <div class="sec-header__sub">/ BACKGROUND</div>
          </div>
          <div class="bg-row">
            <div class="bg-left">
              <div class="bg-quote">"</div>
              <div class="bg-text">
                遗传代谢性脑白质病是一组由遗传因素导致的脑白质代谢异常疾病，临床表型复杂多样，早期症状与多种神经系统疾病高度重叠。传统诊断流程依赖多学科专家联合会诊，平均确诊周期长，漏诊率较高。
                <br /><br />
                为提升诊断效率与准确性，我们设计了这款基于结构化问卷的智能辅助诊断系统。系统整合了国内外权威的遗传代谢性脑白质病诊断指南与专家经验，通过
                12
                道标准化问卷采集关键临床信息，结合影像数据参考，运用多维度评分模型输出疑似疾病排序，为临床医生提供科学、高效、可追溯的诊断参考依据。
              </div>
            </div>
            <div class="bg-right">
              <div class="bg-stat">
                <div class="bg-stat__num">6</div>
                <div class="bg-stat__label">候选疾病类型</div>
              </div>
              <div class="bg-stat">
                <div class="bg-stat__num accent">12</div>
                <div class="bg-stat__label">结构化问卷题目</div>
              </div>
              <div class="bg-stat">
                <div class="bg-stat__num success">9</div>
                <div class="bg-stat__label">App 核心页面</div>
              </div>
              <div class="bg-stat">
                <div class="bg-stat__num warning">12+</div>
                <div class="bg-stat__label">临床维度覆盖</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ======================== UI SHOWCASE ======================== -->
      <section class="ui-showcase section">
        <div class="container">
          <div class="sec-header">
            <div class="sec-header__icon">
              <span
                class="sec-header__icon-dot"
                style="background: var(--success)"
              ></span>
              Product Interfaces
            </div>
            <div class="sec-header__title">界面展示</div>
            <div class="sec-header__sub">/ UI SHOWCASE</div>
          </div>

          <!-- App 端 -->
          <div class="ui-sub-label">
            <div class="ui-sub-label__icon app">📱</div>
            <span class="ui-sub-label__text">App 端</span>
            <span class="ui-sub-label__count">9 pages</span>
          </div>
          <div class="phone-grid">
            <div class="phone-card">
              <div class="phone-card__mockup">
                <div class="phone-frame">
                  <div class="phone-frame__screen">
                    <img
                      class="phone-card__img"
                      alt="首页·未展开侧边栏"
                      loading="lazy"
                      :src="getImage('home-sidebar-collapsed.jpg')"
                      @error="onImageError"
                    />
                  </div>
                </div>
              </div>
              <div class="phone-card__info">
                <div class="phone-card__title">首页 · 未展开侧边栏</div>
                <div class="phone-card__desc">
                  主内容区域，清晰的信息层级与快捷入口布局
                </div>
                <span class="phone-card__tag nav">首页</span>
              </div>
            </div>

            <div class="phone-card">
              <div class="phone-card__mockup">
                <div class="phone-frame">
                  <div class="phone-frame__screen">
                    <img
                      class="phone-card__img"
                      alt="首页·展开侧边栏·已登录"
                      loading="lazy"
                      :src="getImage('home-sidebar-logged-in.jpg')"
                      @error="onImageError"
                    />
                  </div>
                </div>
              </div>
              <div class="phone-card__info">
                <div class="phone-card__title">首页 · 展开侧边栏（已登录）</div>
                <div class="phone-card__desc">
                  已登录状态下展开侧边栏，显示功能菜单与用户信息
                </div>
                <span class="phone-card__tag nav">导航</span>
              </div>
            </div>

            <div class="phone-card">
              <div class="phone-card__mockup">
                <div class="phone-frame">
                  <div class="phone-frame__screen">
                    <img
                      class="phone-card__img"
                      alt="首页·展开侧边栏·未登录"
                      loading="lazy"
                      :src="getImage('home-sidebar-logged-out.jpg')"
                      @error="onImageError"
                    />
                  </div>
                </div>
              </div>
              <div class="phone-card__info">
                <div class="phone-card__title">首页 · 展开侧边栏（未登录）</div>
                <div class="phone-card__desc">
                  未登录状态下的侧边栏展开效果，引导用户完成认证
                </div>
                <span class="phone-card__tag nav">导航</span>
              </div>
            </div>

            <div class="phone-card">
              <div class="phone-card__mockup">
                <div class="phone-frame">
                  <div class="phone-frame__screen">
                    <img
                      class="phone-card__img"
                      alt="第一题·题目有参考图"
                      loading="lazy"
                      :src="getImage('question1-with-ref.jpg')"
                      @error="onImageError"
                    />
                  </div>
                </div>
              </div>
              <div class="phone-card__info">
                <div class="phone-card__title">第1题 · 题目含参考图</div>
                <div class="phone-card__desc">
                  题目展示参考影像图片，答案无参考图辅助
                </div>
                <span class="phone-card__tag quest">问卷</span>
              </div>
            </div>

            <div class="phone-card">
              <div class="phone-card__mockup">
                <div class="phone-frame">
                  <div class="phone-frame__screen">
                    <img
                      class="phone-card__img"
                      alt="第三题·答案有参考图"
                      loading="lazy"
                      :src="getImage('question3-answer-with-ref.jpg')"
                      @error="onImageError"
                    />
                  </div>
                </div>
              </div>
              <div class="phone-card__info">
                <div class="phone-card__title">第3题 · 答案含参考图</div>
                <div class="phone-card__desc">
                  答案选项中嵌入参考图，辅助医生对照判断
                </div>
                <span class="phone-card__tag quest">问卷</span>
              </div>
            </div>

            <div class="phone-card">
              <div class="phone-card__mockup">
                <div class="phone-frame">
                  <div class="phone-frame__screen">
                    <img
                      class="phone-card__img"
                      alt="诊断结论·选择疾病类型"
                      loading="lazy"
                      :src="getImage('conclusion-select-disease.jpg')"
                      @error="onImageError"
                    />
                  </div>
                </div>
              </div>
              <div class="phone-card__info">
                <div class="phone-card__title">诊断结论 · 选择疾病类型</div>
                <div class="phone-card__desc">
                  问卷完成后以概率排序展示疑似疾病列表
                </div>
                <span class="phone-card__tag result">诊断结果</span>
              </div>
            </div>

            <div class="phone-card">
              <div class="phone-card__mockup">
                <div class="phone-frame">
                  <div class="phone-frame__screen">
                    <img
                      class="phone-card__img"
                      alt="诊断结论·详细疾病说明"
                      loading="lazy"
                      :src="getImage('conclusion-disease-detail.jpg')"
                      @error="onImageError"
                    />
                  </div>
                </div>
              </div>
              <div class="phone-card__info">
                <div class="phone-card__title">诊断结论 · 详细疾病说明</div>
                <div class="phone-card__desc">
                  疾病描述 + MRI参照影像 + 概率指标，完整诊断依据
                </div>
                <span class="phone-card__tag result">诊断结果</span>
              </div>
            </div>

            <div class="phone-card">
              <div class="phone-card__mockup">
                <div class="phone-frame">
                  <div class="phone-frame__screen">
                    <img
                      class="phone-card__img"
                      alt="疑似诊断弹窗"
                      loading="lazy"
                      :src="getImage('suspected-diagnosis-popup.jpg')"
                      @error="onImageError"
                    />
                  </div>
                </div>
              </div>
              <div class="phone-card__info">
                <div class="phone-card__title">疑似诊断提示弹窗</div>
                <div class="phone-card__desc">
                  关键节点的模态提醒，突出疑似结果与建议操作
                </div>
                <span class="phone-card__tag alert">提醒</span>
              </div>
            </div>

            <div class="phone-card">
              <div class="phone-card__mockup">
                <div class="phone-frame">
                  <div class="phone-frame__screen">
                    <img
                      class="phone-card__img"
                      alt="全局公告"
                      loading="lazy"
                      :src="getImage('global-announcement.jpg')"
                      @error="onImageError"
                    />
                  </div>
                </div>
              </div>
              <div class="phone-card__info">
                <div class="phone-card__title">全局公告</div>
                <div class="phone-card__desc">
                  顶部横幅公告通知，及时触达系统维护等信息
                </div>
                <span class="phone-card__tag alert">通知</span>
              </div>
            </div>
          </div>
          <!-- /phone-grid -->

          <!-- Web 端 -->
          <div class="ui-sub-label">
            <div class="ui-sub-label__icon web">🖥️</div>
            <span class="ui-sub-label__text">Web 端</span>
            <span class="ui-sub-label__count">3 pages</span>
          </div>
          <div class="web-grid">
            <div class="web-card">
              <div class="web-card__mockup">
                <div class="web-card__chrome">
                  <div class="web-card__chrome-dots">
                    <div class="web-card__chrome-dot red"></div>
                    <div class="web-card__chrome-dot yellow"></div>
                    <div class="web-card__chrome-dot green"></div>
                  </div>
                  <div class="web-card__chrome-url"></div>
                </div>
                <img
                  class="web-card__img"
                  alt="Web端扫码登录"
                  loading="lazy"
                  :src="getImage('login-qrcode.png')"
                  @error="onImageError"
                />
              </div>
              <div class="web-card__info">
                <div class="web-card__title">扫码登录</div>
                <div class="web-card__desc">
                  Web 端二维码扫码登录界面，桌面端适配展示
                </div>
                <span class="web-card__tag auth">登录</span>
              </div>
            </div>

            <div class="web-card">
              <div class="web-card__mockup">
                <div class="web-card__chrome">
                  <div class="web-card__chrome-dots">
                    <div class="web-card__chrome-dot red"></div>
                    <div class="web-card__chrome-dot yellow"></div>
                    <div class="web-card__chrome-dot green"></div>
                  </div>
                  <div class="web-card__chrome-url"></div>
                </div>
                <img
                  class="web-card__img"
                  alt="Web端问卷起始页"
                  loading="lazy"
                  :src="getImage('questionnaire-start.png')"
                  @error="onImageError"
                />
              </div>
              <div class="web-card__info">
                <div class="web-card__title">问卷起始页</div>
                <div class="web-card__desc">
                  Web 端问卷引导页面，大屏宽适配布局
                </div>
                <span class="web-card__tag quest">问卷</span>
              </div>
            </div>

            <div class="web-card">
              <div class="web-card__mockup">
                <div class="web-card__chrome">
                  <div class="web-card__chrome-dots">
                    <div class="web-card__chrome-dot red"></div>
                    <div class="web-card__chrome-dot yellow"></div>
                    <div class="web-card__chrome-dot green"></div>
                  </div>
                  <div class="web-card__chrome-url"></div>
                </div>
                <img
                  class="web-card__img"
                  alt="Web端意见反馈"
                  loading="lazy"
                  :src="getImage('feedback.png')"
                  @error="onImageError"
                />
              </div>
              <div class="web-card__info">
                <div class="web-card__title">意见反馈</div>
                <div class="web-card__desc">
                  Web 端意见反馈表单，收集用户使用建议
                </div>
                <span class="web-card__tag ops">运营</span>
              </div>
            </div>
          </div>
          <!-- /web-grid -->
        </div>
      </section>

      <!-- ======================== BUSINESS FLOW ======================== -->
      <section class="section section--blue">
        <div class="container">
          <div class="sec-header">
            <div class="sec-header__icon">
              <span
                class="sec-header__icon-dot"
                style="background: var(--accent)"
              ></span>
              Analyze Business
            </div>
            <div class="sec-header__title">业务流程</div>
            <div class="sec-header__sub">/ BUSINESS FLOW</div>
          </div>

          <div class="flow-row">
            <div class="flow-intro">
              <div class="flow-intro__text">
                系统围绕医生临床工作流设计，从身份认证到诊断结论输出形成完整的业务闭环。每一步都经过精心设计，确保操作顺畅、数据准确。
              </div>
            </div>

            <div class="flow-diagram">
              <div class="flow-pipeline">
                <div class="flow-node">
                  <div class="flow-node__circle prep">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <rect
                        x="5"
                        y="10"
                        width="14"
                        height="11"
                        rx="2.5"
                        stroke="#0B2D7F"
                        stroke-width="2.2"
                      />
                      <path
                        d="M8 10V6.5a4 4 0 0 1 8 0V10"
                        stroke="#0B2D7F"
                        stroke-width="2.2"
                        stroke-linecap="round"
                      />
                      <circle cx="12" cy="15.5" r="1.5" fill="#0B2D7F" />
                    </svg>
                  </div>
                  <div class="flow-node__title">身份认证</div>
                  <div class="flow-node__desc">
                    扫码 / 密码登录<br />协议确认
                  </div>
                </div>

                <div class="flow-connector">
                  <div class="flow-connector__arrow">→</div>
                </div>

                <div class="flow-node">
                  <div class="flow-node__circle input">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <rect
                        x="5.5"
                        y="3.5"
                        width="13"
                        height="18"
                        rx="2"
                        stroke="#2A79EA"
                        stroke-width="2.2"
                      />
                      <path
                        d="M8.5 2h7a1 1 0 0 1 1 1v2.5h-9V3a1 1 0 0 1 1-1z"
                        stroke="#2A79EA"
                        stroke-width="2.2"
                        stroke-linejoin="round"
                      />
                      <line
                        x1="8.5"
                        y1="11"
                        x2="15.5"
                        y2="11"
                        stroke="#2A79EA"
                        stroke-width="1.8"
                        stroke-linecap="round"
                      />
                      <line
                        x1="8.5"
                        y1="15"
                        x2="14"
                        y2="15"
                        stroke="#2A79EA"
                        stroke-width="1.8"
                        stroke-linecap="round"
                      />
                    </svg>
                  </div>
                  <div class="flow-node__title">信息采集</div>
                  <div class="flow-node__desc">
                    结构化问卷<br />影像数据上传
                  </div>
                </div>

                <div class="flow-connector">
                  <div class="flow-connector__arrow">→</div>
                </div>

                <div class="flow-node">
                  <div class="flow-node__circle think">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M4.5 13c-1.5-.8-2-3.5-.5-5.5s3-2.5 5-3c1-2 3-2.5 4.5-1.5s2.5 2.5 2.5 4.5c1.8 0 3.5 1.2 3 3.8s-2.5 3.5-4 3"
                        stroke="#59A381"
                        stroke-width="2.2"
                        stroke-linecap="round"
                      />
                      <circle
                        cx="7"
                        cy="17"
                        r="5"
                        stroke="#59A381"
                        stroke-width="2.2"
                      />
                      <circle cx="6" cy="15.3" r="1.3" fill="#59A381" />
                      <circle cx="9.5" cy="15.3" r="1.3" fill="#59A381" />
                      <path
                        d="M6 19.5c.8.8 2 .8 2.8 0"
                        stroke="#59A381"
                        stroke-width="1.6"
                        stroke-linecap="round"
                      />
                    </svg>
                  </div>
                  <div class="flow-node__title">智能分析</div>
                  <div class="flow-node__desc">
                    多维度评估<br />匹配算法计算
                  </div>
                </div>

                <div class="flow-connector">
                  <div class="flow-connector__arrow">→</div>
                </div>

                <div class="flow-node">
                  <div class="flow-node__circle output">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <rect
                        x="3.5"
                        y="12"
                        width="4"
                        height="8.5"
                        rx="1"
                        fill="#F2BB4C"
                      />
                      <rect
                        x="10"
                        y="7.5"
                        width="4"
                        height="13"
                        rx="1"
                        fill="#F2BB4C"
                      />
                      <rect
                        x="16.5"
                        y="3"
                        width="4"
                        height="17.5"
                        rx="1"
                        fill="#F2BB4C"
                      />
                    </svg>
                  </div>
                  <div class="flow-node__title">结果输出</div>
                  <div class="flow-node__desc">
                    疾病排序展示<br />病例对照参考
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ======================== FEATURES ======================== -->
      <section class="section">
        <div class="container">
          <div class="sec-header">
            <div class="sec-header__icon">
              <span
                class="sec-header__icon-dot"
                style="background: var(--warning)"
              ></span>
              Feature Overview
            </div>
            <div class="sec-header__title">功能特性</div>
            <div class="sec-header__sub">/ FEATURES</div>
          </div>

          <div class="feat-row">
            <div class="feat-left">
              <div class="feat-topo">
                <div class="feat-topo__ring r1"></div>
                <div class="feat-topo__ring r2"></div>
                <div class="feat-topo__ring r3"></div>
                <div class="feat-topo__center">⚕️</div>
                <div class="feat-topo__node n1">
                  <span
                    class="feat-topo__node-dot"
                    style="background: var(--primary)"
                  ></span
                  >智能诊断
                </div>
                <div class="feat-topo__node n2">
                  <span
                    class="feat-topo__node-dot"
                    style="background: var(--accent)"
                  ></span
                  >问卷引擎
                </div>
                <div class="feat-topo__node n3">
                  <span
                    class="feat-topo__node-dot"
                    style="background: var(--success)"
                  ></span
                  >结果可视化
                </div>
                <div class="feat-topo__node n4">
                  <span
                    class="feat-topo__node-dot"
                    style="background: var(--warning)"
                  ></span
                  >安全认证
                </div>
                <div class="feat-topo__node n5">
                  <span
                    class="feat-topo__node-dot"
                    style="background: var(--error)"
                  ></span
                  >消息通知
                </div>
                <div class="feat-topo__node n6">
                  <span
                    class="feat-topo__node-dot"
                    style="background: var(--primary)"
                  ></span
                  >病例管理
                </div>
              </div>
            </div>

            <div class="feat-right">
              <div class="feat-card">
                <div class="feat-card__icon blue">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M16 2.5v8c0 1.3-.6 2.5-2 3.5m-4 .5c-1.4 1-2 2.2-2 3.5V22"
                      stroke="#0B2D7F"
                      stroke-width="2.2"
                      stroke-linecap="round"
                    />
                    <path
                      d="M8 2.5v8c0 1.3.6 2.5 2 3.5m4 .5c1.4 1 2 2.2 2 3.5V22"
                      stroke="#0B2D7F"
                      stroke-width="2.2"
                      stroke-linecap="round"
                    />
                    <line
                      x1="3"
                      y1="5"
                      x2="21"
                      y2="5"
                      stroke="#0B2D7F"
                      stroke-width="2.2"
                      stroke-linecap="round"
                    />
                    <line
                      x1="3"
                      y1="19"
                      x2="21"
                      y2="19"
                      stroke="#0B2D7F"
                      stroke-width="2.2"
                      stroke-linecap="round"
                    />
                  </svg>
                </div>
                <div>
                  <div class="feat-card__title">智能诊断评估引擎</div>
                  <div class="feat-card__desc">
                    12 道结构化临床问卷 + 多维度评分模型，实时计算候选疾病匹配度
                  </div>
                </div>
              </div>
              <div class="feat-card">
                <div class="feat-card__icon indigo">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <rect
                      x="5.5"
                      y="3.5"
                      width="13"
                      height="18"
                      rx="2"
                      stroke="#2A79EA"
                      stroke-width="2.2"
                    />
                    <path
                      d="M8.5 2h7a1 1 0 0 1 1 1v2.5h-9V3a1 1 0 0 1 1-1z"
                      stroke="#2A79EA"
                      stroke-width="2.2"
                      stroke-linejoin="round"
                    />
                    <line
                      x1="8.5"
                      y1="11"
                      x2="15.5"
                      y2="11"
                      stroke="#2A79EA"
                      stroke-width="1.8"
                      stroke-linecap="round"
                    />
                    <line
                      x1="8.5"
                      y1="15"
                      x2="14"
                      y2="15"
                      stroke="#2A79EA"
                      stroke-width="1.8"
                      stroke-linecap="round"
                    />
                  </svg>
                </div>
                <div>
                  <div class="feat-card__title">全流程信息采集</div>
                  <div class="feat-card__desc">
                    注册登录、问卷作答、参考图上传、病史记录、病例库关联一站式完成
                  </div>
                </div>
              </div>
              <div class="feat-card">
                <div class="feat-card__icon green">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <rect
                      x="3.5"
                      y="12"
                      width="4"
                      height="8.5"
                      rx="1"
                      fill="#59A381"
                    />
                    <rect
                      x="10"
                      y="7.5"
                      width="4"
                      height="13"
                      rx="1"
                      fill="#59A381"
                    />
                    <rect
                      x="16.5"
                      y="3"
                      width="4"
                      height="17.5"
                      rx="1"
                      fill="#59A381"
                    />
                  </svg>
                </div>
                <div>
                  <div class="feat-card__title">可视化结论呈现</div>
                  <div class="feat-card__desc">
                    概率柱状图 + 疾病详情 + MRI影像 + 相似病例多维度对比
                  </div>
                </div>
              </div>
              <div class="feat-card">
                <div class="feat-card__icon amber">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <rect
                      x="5"
                      y="10"
                      width="14"
                      height="11"
                      rx="2.5"
                      stroke="#F2BB4C"
                      stroke-width="2.2"
                    />
                    <path
                      d="M8 10V6.5a4 4 0 0 1 8 0V10"
                      stroke="#F2BB4C"
                      stroke-width="2.2"
                      stroke-linecap="round"
                    />
                    <circle cx="12" cy="15.5" r="1.5" fill="#F2BB4C" />
                  </svg>
                </div>
                <div>
                  <div class="feat-card__title">安全可靠的用户体系</div>
                  <div class="feat-card__desc">
                    双重登录模式 + 协议确认 + 强制更新，全方位保障数据安全
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ======================== DESIGN FOCUS ======================== -->
      <section class="section section--pale">
        <div class="container">
          <div class="sec-header">
            <div class="sec-header__icon">
              <span class="sec-header__icon-dot"></span>
              Design Focus
            </div>
            <div class="sec-header__title">设计理念</div>
            <div class="sec-header__sub">/ DESIGN PHILOSOPHY</div>
          </div>

          <div class="focus-grid">
            <div class="focus-card">
              <div class="focus-card__art hue--purple">
                <img
                  class="focus-art-img"
                  alt=""
                  :src="getImage('focus-art.jpg')"
                  @error="onImageError"
                />
              </div>
              <div class="focus-card__body">
                <div class="focus-card__title">高效</div>
                <div class="focus-card__text">
                  12
                  道核心问卷覆盖关键临床维度，结构化作答替代长篇病历录入。侧边栏快速导航、一键诊断、即时结论，让医生在最短时间内获取诊断参考。
                </div>
              </div>
            </div>

            <div class="focus-card">
              <div class="focus-card__art hue--blue crop--topright">
                <img
                  class="focus-art-img"
                  alt=""
                  :src="getImage('focus-art.jpg')"
                  @error="onImageError"
                />
              </div>
              <div class="focus-card__body">
                <div class="focus-card__title">专业</div>
                <div class="focus-card__text">
                  基于国内外权威诊断指南构建知识库，集成 MRI
                  参考影像对照，输出包含概率分析、疾病详情、相似病例的多维度诊断报告，严谨可靠。
                </div>
              </div>
            </div>

            <div class="focus-card">
              <div class="focus-card__art hue--green crop--botleft">
                <img
                  class="focus-art-img"
                  alt=""
                  :src="getImage('focus-art.jpg')"
                  @error="onImageError"
                />
              </div>
              <div class="focus-card__body">
                <div class="focus-card__title">直观</div>
                <div class="focus-card__text">
                  清晰的视觉层级引导操作路径，进度指示让医生感知位置，缺省页友好提示减少困惑，弹窗在关键节点精准提醒，全程顺畅无阻。
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- ======================== DESIGN SYSTEM ======================== -->
      <section class="section">
        <div class="container">
          <div class="sec-header">
            <div class="sec-header__icon">
              <span
                class="sec-header__icon-dot"
                style="background: var(--accent)"
              ></span>
              Design System
            </div>
            <div class="sec-header__title">设计规范</div>
            <div class="sec-header__sub">/ DESIGN SYSTEM</div>
          </div>

          <div class="ds-grid">
            <!-- Left: Color System -->
            <div>
              <div class="ds-block">
                <div class="ds-block__title">🎨 色彩系统 / Color Palette</div>
                <div class="ds-colors">
                  <div class="ds-swatch">
                    <div class="ds-swatch__color" style="background: #0b2d7f">
                      <span class="ds-swatch__hex">#0B2D7F</span>
                    </div>
                    <div class="ds-swatch__name">Primary</div>
                    <div class="ds-swatch__label">品牌主色</div>
                  </div>
                  <div class="ds-swatch">
                    <div class="ds-swatch__color" style="background: #2a79ea">
                      <span class="ds-swatch__hex">#2A79EA</span>
                    </div>
                    <div class="ds-swatch__name">Accent</div>
                    <div class="ds-swatch__label">强调色</div>
                  </div>
                  <div class="ds-swatch">
                    <div class="ds-swatch__color" style="background: #082263">
                      <span class="ds-swatch__hex">#082263</span>
                    </div>
                    <div class="ds-swatch__name">Dark</div>
                    <div class="ds-swatch__label">深色变体</div>
                  </div>
                  <div class="ds-swatch">
                    <div class="ds-swatch__color" style="background: #7baff0">
                      <span class="ds-swatch__hex">#7BAFF0</span>
                    </div>
                    <div class="ds-swatch__name">Soft</div>
                    <div class="ds-swatch__label">浅色变体</div>
                  </div>
                </div>
              </div>

              <div class="ds-block">
                <div class="ds-block__title">🟢 功能色 / Semantic Colors</div>
                <div class="ds-colors">
                  <div class="ds-swatch">
                    <div class="ds-swatch__color" style="background: #59a381">
                      <span class="ds-swatch__hex">#59A381</span>
                    </div>
                    <div class="ds-swatch__name">Success</div>
                    <div class="ds-swatch__label">成功/诊断通过</div>
                  </div>
                  <div class="ds-swatch">
                    <div class="ds-swatch__color" style="background: #f2bb4c">
                      <span class="ds-swatch__hex">#F2BB4C</span>
                    </div>
                    <div class="ds-swatch__name">Warning</div>
                    <div class="ds-swatch__label">警示/疑似提醒</div>
                  </div>
                  <div class="ds-swatch">
                    <div class="ds-swatch__color" style="background: #fc6d6d">
                      <span class="ds-swatch__hex">#FC6D6D</span>
                    </div>
                    <div class="ds-swatch__name">Error</div>
                    <div class="ds-swatch__label">错误/操作失败</div>
                  </div>
                  <div class="ds-swatch">
                    <div
                      class="ds-swatch__color"
                      style="background: rgba(11, 45, 127, 0.06)"
                    >
                      <span
                        class="ds-swatch__hex"
                        style="color: var(--text-muted); text-shadow: none"
                        >6% Opacity</span
                      >
                    </div>
                    <div class="ds-swatch__name">Pale Bg</div>
                    <div class="ds-swatch__label">浅蓝背景</div>
                  </div>
                </div>
              </div>

              <div class="ds-block">
                <div class="ds-block__title">⚪ 中性色 / Neutral Palette</div>
                <div class="ds-colors">
                  <div class="ds-swatch">
                    <div class="ds-swatch__color" style="background: #0f172a">
                      <span class="ds-swatch__hex">#0F172A</span>
                    </div>
                    <div class="ds-swatch__name">Text Dark</div>
                    <div class="ds-swatch__label">主标题</div>
                  </div>
                  <div class="ds-swatch">
                    <div class="ds-swatch__color" style="background: #475569">
                      <span class="ds-swatch__hex">#475569</span>
                    </div>
                    <div class="ds-swatch__name">Text Sec.</div>
                    <div class="ds-swatch__label">正文/描述</div>
                  </div>
                  <div class="ds-swatch">
                    <div class="ds-swatch__color" style="background: #e2e8f0">
                      <span
                        class="ds-swatch__hex"
                        style="color: var(--text-muted); text-shadow: none"
                        >#E2E8F0</span
                      >
                    </div>
                    <div class="ds-swatch__name">Border</div>
                    <div class="ds-swatch__label">分隔线/边框</div>
                  </div>
                  <div class="ds-swatch">
                    <div class="ds-swatch__color" style="background: #f8fafe">
                      <span
                        class="ds-swatch__hex"
                        style="color: var(--text-muted); text-shadow: none"
                        >#F8FAFE</span
                      >
                    </div>
                    <div class="ds-swatch__name">Bg Light</div>
                    <div class="ds-swatch__label">浅色背景</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right: Typography & Tokens -->
            <div>
              <div class="ds-block">
                <div class="ds-block__title">🔤 字体排版 / Typography</div>
                <div class="ds-type-row">
                  <span class="ds-type__spec">36px · 900</span>
                  <span
                    class="ds-type__sample"
                    style="font-size: 36px; font-weight: 900"
                    >4XL Heading</span
                  >
                </div>
                <div class="ds-type-row">
                  <span class="ds-type__spec">30px · 900</span>
                  <span
                    class="ds-type__sample"
                    style="font-size: 30px; font-weight: 900"
                    >3XL Heading</span
                  >
                </div>
                <div class="ds-type-row">
                  <span class="ds-type__spec">24px · 800</span>
                  <span
                    class="ds-type__sample"
                    style="font-size: 24px; font-weight: 800"
                    >2XL · 区块标题</span
                  >
                </div>
                <div class="ds-type-row">
                  <span class="ds-type__spec">18px · 700</span>
                  <span
                    class="ds-type__sample"
                    style="font-size: 18px; font-weight: 700"
                    >XL · 卡片标题</span
                  >
                </div>
                <div class="ds-type-row">
                  <span class="ds-type__spec">16px · 500</span>
                  <span
                    class="ds-type__sample"
                    style="font-size: 16px; font-weight: 500"
                    >Base · 正文内容</span
                  >
                </div>
                <div class="ds-type-row">
                  <span class="ds-type__spec">14px · 500</span>
                  <span
                    class="ds-type__sample"
                    style="font-size: 14px; font-weight: 500"
                    >SM · 辅助说明</span
                  >
                </div>
                <div class="ds-type-row">
                  <span class="ds-type__spec">12px · 500</span>
                  <span
                    class="ds-type__sample"
                    style="font-size: 12px; font-weight: 500"
                    >XS · 标签/备注</span
                  >
                </div>
                <div class="ds-type-row">
                  <span class="ds-type__spec">11px · 400</span>
                  <span
                    class="ds-type__sample"
                    style="font-size: 11px; font-weight: 400"
                    >Caption</span
                  >
                </div>
              </div>

              <div class="ds-block">
                <div class="ds-block__title">📐 圆角 / Border Radius</div>
                <div class="ds-token-grid">
                  <div class="ds-token">
                    <div class="ds-token__value">12px</div>
                    <div class="ds-token__label">默认圆角</div>
                  </div>
                  <div class="ds-token">
                    <div class="ds-token__value">16px</div>
                    <div class="ds-token__label">大圆角</div>
                  </div>
                  <div class="ds-token">
                    <div class="ds-token__value">20px</div>
                    <div class="ds-token__label">超大圆角</div>
                  </div>
                  <div class="ds-token">
                    <div class="ds-token__value">28px</div>
                    <div class="ds-token__label">手机样机</div>
                  </div>
                </div>
              </div>

              <div class="ds-block">
                <div class="ds-block__title">
                  📏 间距系统 / Spacing (8pt Grid)
                </div>
                <div class="ds-token-grid">
                  <div class="ds-token">
                    <div class="ds-token__value">4px</div>
                    <div class="ds-token__label">XS 间距</div>
                  </div>
                  <div class="ds-token">
                    <div class="ds-token__value">8px</div>
                    <div class="ds-token__label">SM 间距</div>
                  </div>
                  <div class="ds-token">
                    <div class="ds-token__value">16px</div>
                    <div class="ds-token__label">MD 间距</div>
                  </div>
                  <div class="ds-token">
                    <div class="ds-token__value">24px</div>
                    <div class="ds-token__label">LG 间距</div>
                  </div>
                </div>
              </div>

              <div class="ds-block" style="margin-bottom: 0">
                <div class="ds-block__title">🌓 投影 / Elevation</div>
                <div class="ds-token-grid">
                  <div class="ds-token">
                    <div class="ds-token__value">SM</div>
                    <div class="ds-token__label">悬停微提</div>
                  </div>
                  <div class="ds-token">
                    <div class="ds-token__value">MD</div>
                    <div class="ds-token__label">卡片悬浮</div>
                  </div>
                  <div class="ds-token">
                    <div class="ds-token__value">LG</div>
                    <div class="ds-token__label">面板弹出</div>
                  </div>
                  <div class="ds-token">
                    <div class="ds-token__value">XL</div>
                    <div class="ds-token__label">模态弹窗</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Page Summary -->
    <section class="page-summary">
      <div class="page-summary__inner">
        <p class="page-summary__text fontRegular">{{ detailIntroduction }}</p>
        <div class="page-summary__preview">
          <img
            v-for="(img, index) in previewImages"
            :key="'preview-' + index"
            :src="img"
            alt=""
          />
        </div>
        <div class="page-summary__back fontRegular" @click="goBack">返回</div>
      </div>
    </section>

    <!-- Related Cases -->
    <section class="related-cases">
      <div class="related-cases__inner">
        <div class="related-cases__title">
          <img :src="caseListText" alt="相关案例" />
        </div>
        <div class="related-cases__grid">
          <list-card
            v-for="(item, index) in relatedCaseList"
            :key="'related-case-' + index"
            class="noPadding"
            :src="item.image"
            :routeQuery="item.routeQuery"
            :keyIndex="index"
            :showDownName="false"
            :isRichHtml="true"
            :showFreeTrialTag="item.showFreeTrialTag"
            :backgroundColor="item.backgroundColor"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import ListCard from "@/components/list-card/index.vue";
import { initRichHtmlReveal } from "../utils/useRichHtmlReveal";
import imageMap from "./images";

const getImage = (name) => imageMap[name] || "";

const bannerScreenshot = getImage("conclusion-disease-detail.jpg");

const onImageError = (event) => {
  const target = event.target;
  if (target) {
    target.style.display = "none";
  }
};

const introduceText = require("@/assets/images/introduce-text.png");
const caseListText = require("@/assets/images/case-list-text.png");

const projectIntroduction =
  "面向医疗机构的智能辅助诊断平台，通过结构化问卷采集患者临床信息与影像数据，基于多维度评估模型对遗传代谢性脑白质病进行智能分析与疑似判断。为医生提供可靠诊断参考，缩短确诊周期，降低漏诊风险。";

const detailIntroduction =
  "云效科技崇尚创新，专注研发，拥有一支国内领先的技术团队，并凭借专业知识强、业务技术精、服务质量高、精神面貌佳等特点得到用户的一致赞誉。在软件开发的专业领域，云效科技与行业内诸多知名企业保持有长期稳定的合作关系。";

const previewImages = [
  require("@/assets/images/list-case/attract-investment/image-1.png"),
];

const relatedCaseList = [
  {
    image: require("@/assets/images/project-list-card/project-36.png"),
    backgroundColor: "#0FBFAE",
    routeQuery: {
      name: "intelligentElectricAdjustmentSystem",
      isRichHtml: true,
    },
  },
  {
    image: require("@/assets/images/project-list-card/project-39.png"),
    backgroundColor: "#1C69C7",
    routeQuery: { name: "intelligentPetFeeder", isRichHtml: true },
  },
  {
    image: require("@/assets/images/project-list-card/project-37.png"),
    backgroundColor: "#09C9B0",
    routeQuery: { name: "lovableHome", isRichHtml: true },
  },
];

const router = useRouter();
const rootRef = ref(null);

const goBack = () => {
  router.go(-1);
};

onMounted(() => {
  initRichHtmlReveal(rootRef.value);
});
</script>

<style lang="scss" src="./case.scss"></style>
<style lang="scss" src="./banner.scss"></style>
<style lang="scss" src="./layout.scss"></style>
